import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Save, RotateCcw } from 'lucide-react';

import Button from '@components/common/Button';
import { Checkbox, Input } from '@components/common';
import SettingsSectionHeader from '@components/features/data-management-settings/SettingsSectionHeader';
import { useReportDirty } from '@components/features/data-management-settings/useReportDirty';
import type { SettingsPaneProps } from '@components/features/data-management-settings/settingsNav';
import type { SystemParameter } from '@app-types/system-parameter';
import { systemParameterService } from '@services';
import { getApiErrorMessage } from '@utils/apiError';
import { showToast } from '@utils/toast';
import styles from './SystemParametersSettings.module.css';

function formatParameterKey(key: string): string {
  return key.replace(/([A-Z])/g, ' $1').trim();
}

function validateParameterValue(parameter: SystemParameter, value: string): string | null {
  if (value.trim() === '') {
    return 'Value is required';
  }

  if (parameter.type === 'Number') {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue) || !/^\d+$/.test(value.trim())) {
      return 'Enter a whole number';
    }
    if (numericValue < 0) {
      return 'Value must be at least 0';
    }
  }

  return null;
}

const SystemParametersSettings: React.FC<SettingsPaneProps> = ({ onDirtyChange }) => {
  const [parameters, setParameters] = useState<SystemParameter[]>([]);
  const [draftValues, setDraftValues] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadParameters = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(false);
      const data = await systemParameterService.getSystemParameters();
      setParameters(data);
      setDraftValues(Object.fromEntries(data.map((parameter) => [parameter.key, parameter.value])));
      setFieldErrors({});
    } catch {
      setLoadError(true);
      showToast.error('Failed to load system parameters');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadParameters();
  }, [loadParameters]);

  const dirtyKeys = useMemo(
    () => parameters.filter((parameter) => (draftValues[parameter.key] ?? parameter.value) !== parameter.value).map((parameter) => parameter.key),
    [draftValues, parameters]
  );

  useReportDirty(dirtyKeys.length > 0, onDirtyChange);

  const handleValueChange = (key: string, value: string) => {
    setDraftValues((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      if (!prev[key]) {
        return prev;
      }
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleReset = () => {
    setDraftValues(Object.fromEntries(parameters.map((parameter) => [parameter.key, parameter.value])));
    setFieldErrors({});
  };

  const handleSave = async () => {
    const nextErrors: Record<string, string> = {};
    const dirtyParameters = parameters.filter((parameter) => dirtyKeys.includes(parameter.key));

    for (const parameter of dirtyParameters) {
      const error = validateParameterValue(parameter, draftValues[parameter.key] ?? parameter.value);
      if (error) {
        nextErrors[parameter.key] = error;
      }
    }

    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      return;
    }

    try {
      setSaving(true);
      const updatedList = await Promise.all(
        dirtyParameters.map((parameter) =>
          systemParameterService.updateSystemParameter(parameter.key, (draftValues[parameter.key] ?? parameter.value).trim())
        )
      );
      const updatedByKey = Object.fromEntries(updatedList.map((item) => [item.key, item]));
      setParameters((prev) => prev.map((item) => updatedByKey[item.key] ?? item));
      setDraftValues((prev) => ({
        ...prev,
        ...Object.fromEntries(updatedList.map((item) => [item.key, item.value])),
      }));
      showToast.success('Timing thresholds saved');
    } catch (error) {
      showToast.error(getApiErrorMessage(error, 'Failed to save system parameters'));
    } finally {
      setSaving(false);
    }
  };

  const groups = useMemo((): Array<[string, SystemParameter[]]> => {
    const grouped = new Map<string, SystemParameter[]>();
    for (const parameter of parameters) {
      const groupName = parameter.usedBy || 'Other';
      const items = grouped.get(groupName) ?? [];
      items.push(parameter);
      grouped.set(groupName, items);
    }
    return Array.from(grouped.entries());
  }, [parameters]);

  if (loading) {
    return (
      <div className={styles.loading} data-test-id="system-parameters-loading">
        <Loader2 size={48} className="spin" />
        <p>Loading timing thresholds...</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={styles.error} data-test-id="system-parameters-error">
        Failed to load timing thresholds
      </div>
    );
  }

  return (
    <div className={styles.container} data-test-id="system-parameters-settings">
      <SettingsSectionHeader
        sticky
        title="Timing Thresholds"
        description="Configure day-based thresholds used by scheduled jobs and automated processes."
        actions={
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReset}
              disabled={dirtyKeys.length === 0 || saving}
              dataTestId="system-parameters-reset"
            >
              <RotateCcw size={14} />
              Reset
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => void handleSave()}
              disabled={dirtyKeys.length === 0 || saving || Object.keys(fieldErrors).length > 0}
              loading={saving}
              loadingText="Saving..."
              dataTestId="system-parameters-save"
            >
              <Save size={14} />
              Save changes
            </Button>
          </>
        }
      />

      {parameters.length === 0 ? (
        <div className={styles.empty} data-test-id="system-parameters-empty">
          No system parameters are configured.
        </div>
      ) : (
        groups.map(([groupName, items]) => (
          <section key={groupName} className={styles.group}>
            <h3 className={styles.groupTitle}>{groupName}</h3>
            <div className={styles.rowList}>
              {items.map((parameter) => {
                const draftValue = draftValues[parameter.key] ?? parameter.value;
                const validationError = fieldErrors[parameter.key];

                return (
                  <div
                    key={parameter.key}
                    className={styles.row}
                    data-test-id={`system-parameter-${parameter.key}`}
                  >
                    <div className={styles.rowMeta}>
                      <div className={styles.rowName}>{formatParameterKey(parameter.key)}</div>
                      {parameter.description && (
                        <p className={styles.rowDescription}>{parameter.description}</p>
                      )}
                    </div>
                    <div className={styles.rowControl}>
                      {parameter.type === 'Boolean' ? (
                        <Checkbox
                          label="Enabled"
                          checked={draftValue === 'true'}
                          onChange={(event) =>
                            handleValueChange(parameter.key, event.target.checked ? 'true' : 'false')
                          }
                          dataTestId={`system-parameter-value-${parameter.key}`}
                        />
                      ) : (
                        <Input
                          type={parameter.type === 'Number' ? 'number' : 'text'}
                          min={parameter.type === 'Number' ? 0 : undefined}
                          step={parameter.type === 'Number' ? 1 : undefined}
                          value={draftValue}
                          onChange={(event) => handleValueChange(parameter.key, event.target.value)}
                          error={validationError}
                          rightIcon={parameter.type === 'Number' ? <span className={styles.suffix}>days</span> : undefined}
                          wrapperClassName={styles.inputField}
                          dataTestId={`system-parameter-value-${parameter.key}`}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))
      )}
    </div>
  );
};

export default SystemParametersSettings;
